import type { ComparisonFilter, NestedLogicalFilter, QueryStringInput, SortDirection } from '@hello-aria/query-string'
import { Equal, In, isComparisonFilter } from '@hello-aria/query-string'
import type {
    ComparisonFilterInput,
    FilterParamsInput,
    NestedLogicalFilterInput
} from '@hello-aria/query-string/dist/stringify'
import type { InfiniteData } from '@tanstack/react-query'
import { keepPreviousData } from '@tanstack/react-query'
import { endOfDay, startOfDay } from 'date-fns'
import { isArray, isEmpty, isNil, omitBy } from 'lodash'

import type { PartialRecord } from '@/shared/utils/typesUtils'
import axios from 'axios'
import { DEFAULT_PAGE_SIZE } from '@/shared/enums/constants'
import type { RequestResponse } from '@/shared/services/requestServices'
import { removeWhiteSpace } from './stringUtils'
import { HttpError, isAbortError } from '@hello-aria/makalu-api-client'

/**
 * The v5 `placeholderData` for hooks that take a keep-previous-data option, so a table does not blank between pages.
 */
export const keepPreviousDataPlaceholder = (keep: boolean | undefined) => (keep ? keepPreviousData : undefined)

type UnwrapArray<T> = T extends readonly (infer U)[] ? U : T
type NonU<T> = Exclude<T, undefined | null>

export function flattenInfiniteData<T>(
    data: InfiniteData<RequestResponse<T> | undefined> | undefined
): Array<UnwrapArray<T>> | undefined {
    if (!data?.pages) return undefined

    const bodies = data.pages
        .filter((p): p is NonU<typeof p> => !isNil(p))
        .map((p) => p.body)
        .filter((b): b is NonU<typeof b> => !isNil(b)) as Array<T>

    if (Array.isArray(bodies[0])) {
        return bodies.flat()
    }
    // eslint-disable-next-line typescript/no-unsafe-type-assertion -- when a page body is not an array, UnwrapArray<T> is T itself
    return bodies as unknown as Array<UnwrapArray<T>>
}

export const getInfiniteDataTotalCount = <T>(
    data: InfiniteData<RequestResponse<T> | undefined> | undefined
): number => {
    return data?.pages?.[0]?.pagination?.totalCount ?? 0
}

export type MapSortByInput = {
    id: string
    desc?: boolean
}[]

type MapSortBy = {
    path: string
    direction: SortDirection
}[]

export const mapSortBy = (sortBy: MapSortByInput): MapSortBy => {
    const { id, desc } = sortBy[0]
    return [{ path: id, direction: desc ? 'desc' : 'asc' }]
}

export interface MapPaginationInput {
    after?: string
    before?: string
    first?: number
    last?: number
}

export interface MapPagination {
    after?: string
    before?: string
    first: number
    last?: number
}

export const mapPagination = (pagination: MapPaginationInput): MapPagination => {
    const { first, ...rest } = pagination

    return {
        first: first ?? DEFAULT_PAGE_SIZE,
        ...omitBy(rest, (value) => isNil(value) || isEmpty(value))
    }
}

type SearchInputKeys = 'number' | 'name' | 'date' | 'email' | 'exact'

export interface MapSearchInput {
    value: string | undefined
    path: PartialRecord<SearchInputKeys, string | string[]>
}

const SEARCH_LIST_SEPARATORS = /[\s,;]+/
// An explicit separator means the user clearly typed/pasted a list. A bare space
// is ambiguous (e.g. a multi-word debtor name), so it is handled more leniently.
const EXPLICIT_LIST_SEPARATOR = /[,;\r\n]/
// Up to this many space-separated tokens could be a multi-word name, so we keep
// the fuzzy search alongside the exact match. Beyond it, it is clearly a pasted
// list — matching exactly only, which also keeps the request URL small.
const AMBIGUOUS_SPACE_TOKEN_LIMIT = 3

// A single amount (e.g. "100 000" or "100 000,30") is only digits, spaces and at
// most one decimal comma — keep it on the regular `number` search path instead of
// splitting it into a list. Two or more commas is always a list.
const looksLikeAmount = (value: string): boolean =>
    (value.match(/,/g) || []).length <= 1 && /^[\d\s,]+$/.test(value.trim())

/**
 * Split a pasted search value into a deduped list of trimmed tokens.
 * Supports whitespace (incl. newlines), comma and semicolon separators so users
 * can paste a list straight from a spreadsheet column or a separated line.
 */
export const parseSearchList = (value: string): string[] => [
    ...new Set(
        value
            .split(SEARCH_LIST_SEPARATORS)
            .map((token) => token.trim())
            .filter((token) => !isEmpty(token))
    )
]

export const mapSearch = (searchValue: MapSearchInput | undefined): QueryStringInput['filters'] => {
    if (searchValue) {
        const { value, path } = searchValue
        if (isNil(value) || isEmpty(value) || isNil(path)) return undefined

        const filters = Object.entries(path).reduce(
            (acc, [key, searchType]) => {
                const typedKey = key

                if (typedKey === 'email') {
                    const sanitizedValue = removeWhiteSpace(value)

                    acc?.push({
                        type: 'comparison',
                        path: 'email',
                        value: sanitizedValue ? '%' + sanitizedValue + '%' : '',
                        operator: 'like'
                    })
                }

                if (typedKey === 'number') {
                    const valueWithoutSpace = removeWhiteSpace(value)
                    const valueWithDotInsteadOfComma = valueWithoutSpace.replace(/,/g, '.')
                    const valueWithoutTrailingZero = Number(valueWithDotInsteadOfComma)

                    if (!Number.isNaN(valueWithoutTrailingZero)) {
                        acc?.push({
                            type: 'comparison',
                            path: searchType,
                            value: valueWithoutTrailingZero ? '%' + String(valueWithoutTrailingZero) + '%' : '',
                            operator: 'like'
                        })
                    }
                }

                if (typedKey === 'name') {
                    const escapedValue = value.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')
                    const regex = new RegExp(`.*${escapedValue}.*`, 'ig')

                    acc?.push({
                        type: 'comparison',
                        path: searchType,
                        value: String(regex),
                        operator: 'regex'
                    })
                }

                if (typedKey === 'date') {
                    const isValidDate = value.match(/^(\d{2})\/(\d{2})\/(\d{4})$/)

                    if (isValidDate) {
                        const [day, month, year] = value.split('/')

                        const startDate = startOfDay(new Date(Number(year), Number(month) - 1, Number(day))).getTime()
                        const endDate = endOfDay(new Date(Number(year), Number(month) - 1, Number(day))).getTime()

                        if (!Number.isNaN(startDate) && !Number.isNaN(endDate)) {
                            acc?.push({
                                type: 'logical',
                                operator: 'and',
                                filters: [
                                    {
                                        type: 'comparison',
                                        path: 'billDate',
                                        value: startDate.toString(),
                                        operator: 'gte'
                                    },
                                    {
                                        type: 'comparison',
                                        path: 'billDate',
                                        value: endDate.toString(),
                                        operator: 'lte'
                                    }
                                ]
                            })
                        }
                    }
                }

                return acc
            },
            [] as (ComparisonFilterInput | NestedLogicalFilterInput)[]
        )

        if (!isNil(path.exact) && !looksLikeAmount(value)) {
            const list = parseSearchList(value)
            if (list.length > 1) {
                const exactPaths = isArray(path.exact) ? path.exact : [path.exact]
                const inFilters = exactPaths.map((exactPath) => In(exactPath, list))

                // An explicit separator, or more tokens than a name could have,
                // means an unambiguous list → match exactly only. A short
                // space-separated value could be a multi-word name → keep the fuzzy
                // search alongside the exact match.
                const isExactOnly = EXPLICIT_LIST_SEPARATOR.test(value) || list.length > AMBIGUOUS_SPACE_TOKEN_LIMIT

                return [
                    {
                        type: 'logical',
                        operator: 'or',
                        filters: isExactOnly ? inFilters : [...filters, ...inFilters]
                    }
                ]
            }
        }

        return [
            {
                type: 'logical',
                operator: 'or',
                filters
            }
        ]
    }
}

const resolveFilters = (
    search: MapSearchInput | undefined,
    filter: FilterParamsInput | undefined
): FilterParamsInput[] | undefined => {
    if (isNil(search)) {
        return isNil(filter) ? undefined : [filter]
    }

    const formattedSearch = mapSearch(search)

    if (isNil(formattedSearch)) {
        return isNil(filter) || isEmpty(filter) ? undefined : [filter]
    }

    if (isNil(filter)) {
        return formattedSearch
    }

    formattedSearch.push(filter)
    return formattedSearch
}

interface MapQuery {
    sortBy?: MapSortByInput
    pagination?: MapPaginationInput
    search?: MapSearchInput
    filter?: FilterParamsInput
    params?: QueryStringInput['params']
}

export const mapQuery = (query: MapQuery): QueryStringInput => {
    const { sortBy, pagination, search, params, filter } = query

    return {
        sorts: !isNil(sortBy) && !isEmpty(sortBy) ? mapSortBy(sortBy) : undefined,
        pagination: !isNil(pagination) ? mapPagination(pagination) : undefined,
        filters: resolveFilters(search, filter),
        params
    }
}

export const getPageSize = (query: QueryStringInput | undefined): number => {
    if (query?.pagination?.type === 'forward' && query?.pagination?.first) {
        return query.pagination.first
    }

    if (query?.pagination?.type === 'backward' && query?.pagination?.last) {
        return query.pagination.last
    }

    return DEFAULT_PAGE_SIZE
}

export const resolveQueryFilters = (additionalFilters?: FilterParamsInput[]): NestedLogicalFilter => {
    const filters: ComparisonFilter[] = []

    if (additionalFilters) {
        const comparisonFilters = additionalFilters
            .filter((filter): filter is ComparisonFilterInput => filter.type === 'comparison')
            .map((filter) => {
                const { type, path, operator, value } = filter
                // eslint-disable-next-line typescript/no-unsafe-type-assertion -- the pages pass comparison filters with one path and an operator, which the API reads as is
                return { type, path, operator, value } as ComparisonFilter
            })

        filters.push(...comparisonFilters)
    }

    return { type: 'logical', operator: 'and', filters }
}

/**
 * Adds a company filter to the query object to ensure data is filtered properly based on the user's access level.
 *
 * For "godmode" users, since the endpoint is private, an `owner.id = companyId` filter is explicitly added
 * to the query to ensure data is scoped correctly. For regular users, the backend automatically scopes
 * the data based on the token, so no additional filter is required.
 *
 * @param companyId - The ID of the company to filter by. If the value is 'me', no filter is applied.
 * @param query - An optional query object that may already contain filters.
 * @param field - The field to filter on. This is used to check if the filter already exists.
 * @returns A new query object with the company filter applied, if necessary.
 */
export const handlePrivateEndpointCompanyFilter = (
    companyId: string,
    query?: QueryStringInput,
    field = 'owner.id'
): QueryStringInput => {
    const companyFilter = companyId === 'me' ? undefined : Equal(field, companyId)
    const filters = query?.filters || []

    if (companyFilter && !filters.some((filter) => isComparisonFilter(filter) && filter.path === field)) {
        filters.push(companyFilter)
    }

    return { ...query, filters }
}
export const isErrorWithCode = (error: unknown): error is { code: string; status?: number; message?: string } => {
    return (
        typeof error === 'object' &&
        error !== null &&
        'code' in error &&
        typeof (error as { code?: unknown }).code === 'string'
    )
}

export const handleHttpError = (context: string, error: unknown): never => {
    console.error(`[${context}] Handling error:`, error)

    if (isAbortError(error)) {
        throw error
    }

    if (axios.isAxiosError(error)) {
        const status = error.response?.status ?? 500
        const data = error.response?.data

        throw new HttpError({
            status,
            code: typeof data?.code === 'string' ? data.code : 'AXIOS_ERROR',
            message: typeof data?.message === 'string' ? data.message : 'Unexpected server error'
        })
    }

    if (isErrorWithCode(error)) {
        throw new HttpError({
            status: error.status ?? 500,
            code: error.code,
            message: typeof error.message === 'string' ? error.message : 'Unknown error occurred'
        })
    }

    throw new HttpError({
        status: 500,
        code: 'UNKNOWN_ERROR',
        message: error instanceof Error ? error.message : 'Unknown error occurred'
    })
}
